import type { AppStrings } from './types.js';
import { plural } from './plural.js';

export const es: AppStrings = {
  // COMMON
  error_title: 'Error',
  cancel: 'Cancelar',
  retry: 'Reintentar',
  back_arrow: '← Volver',

  // ASK SCREEN
  ask_box_unavailable: 'Box no disponible',
  ask_box_unavailable_desc: 'El enlace puede estar desactualizado o el dueño cerró su box.',
  ask_create_own_box: 'Crear mi propio box',
  ask_sent_title: 'Enviado ✅',
  ask_sent_desc: 'Tu pregunta anónima fue entregada al dueño del box.',
  ask_anonymous_hint: 'Tu pregunta se enviará de forma anónima.',
  ask_placeholder: 'Escribe tu pregunta…',
  ask_send: 'Enviar',
  ask_sending: 'Enviando…',
  ask_error_rate_limit: 'Demasiadas solicitudes — espera un momento.',
  ask_error_restart: 'Por favor, reinicia la Mini App.',
  ask_error_load: 'No se pudo cargar el box.',
  ask_error_send: 'No se pudo enviar la pregunta.',
  ask_error_banned: 'Estás bloqueado en la app.',
  ask_banned_appeal_btn: 'Apelar',
  ask_appeal_sent: 'Apelación enviada. La revisaremos manualmente.',

  // AGE GATE
  age_gate_title: 'Solo mayores de 18',
  age_gate_desc:
    'Estás enviando una pregunta anónima. Sé respetuoso: están prohibidos el acoso, las amenazas, el contenido sexual que involucre menores y cualquier contenido que viole la ley. El anonimato no implica impunidad — las infracciones se reportan a moderación. Al enviar una pregunta, confirmas que tienes 18+ y aceptas las reglas.',
  age_gate_confirm: 'Tengo 18+ — continuar',

  // INBOX SCREEN
  inbox_title: 'Tu box',
  box_link_accordion: 'Enlace de invitación',
  inbox_notifications_ask: '🔔 ¿Permitir que el bot te notifique sobre nuevas preguntas?',
  inbox_notifications_allow: 'Permitir',
  inbox_notifications_dismiss_label: 'Descartar',
  inbox_box_toggle_disable: 'Desactivar preguntas',
  inbox_box_toggle_enable: 'Activar preguntas',
  inbox_box_disabled_warning: 'Las preguntas están desactivadas — no se aceptarán nuevas.',
  inbox_share_hint: 'Comparte el enlace para que tus amigos te hagan preguntas anónimas.',
  inbox_copy: 'Copiar',
  inbox_copied: 'Copiado',
  inbox_copy_error: 'Error',
  inbox_no_deeplink: 'El enlace aparecerá después del deploy.',
  inbox_invite_story: 'Invitar por Story',
  inbox_invite_chat: 'Invitar por chat',
  inbox_card_rendering: 'La tarjeta de invitación aún se está generando.',
  inbox_story_unsupported:
    'Las Stories no están disponibles en esta versión de Telegram. Prueba "por chat".',
  inbox_story_error: 'No se pudo abrir la Story.',
  inbox_chat_unsupported:
    'El envío por chat no está disponible en esta versión de Telegram. Actualiza tu cliente.',
  inbox_chat_error: 'No se pudo compartir. Inténtalo de nuevo.',
  inbox_chat_prepare_error: 'No se pudo preparar la invitación.',
  inbox_filter_incoming: 'Entrantes',
  inbox_filter_hidden: 'Ocultas',
  inbox_filter_blocked: 'Bloqueadas',
  inbox_filter_empty: 'Todavía nada aquí.',
  inbox_acc_unanswered: 'Sin responder',
  inbox_acc_answered: 'Respondidas',
  inbox_acc_hidden: 'Ocultas',
  inbox_acc_blocked: 'Bloqueadas',
  inbox_empty: 'Aún no hay preguntas. Comparte el enlace de arriba.',
  inbox_load_more: 'Cargar más',
  inbox_load_more_error: 'No se pudo cargar la siguiente página.',
  inbox_error_load: 'No se pudo cargar el box.',
  inbox_rate_limit: 'Demasiadas solicitudes — espera un momento.',
  inbox_toggle_error: 'No se pudo cambiar el estado del box.',

  // MESSAGE BOX (Трек D)
  message_tagline: 'Escríbeme de forma anónima',
  message_placeholder: 'Escribe un mensaje…',
  message_anonymous_hint: 'Tu mensaje se enviará de forma anónima.',
  message_sent_title: 'Enviado ✅',
  message_sent_desc: 'Tu mensaje anónimo ha sido entregado.',
  message_owner_entry: 'Escríbeme',
  message_share_hint: 'Comparte el enlace para que cualquiera pueda enviarte un mensaje anónimo.',
  message_inbox_open: 'Mensajes recibidos',
  message_inbox_title: 'Mensajes',
  message_inbox_empty: 'Aún no hay mensajes. Comparte tu enlace de arriba.',

  // ANSWER SCREEN
  answer_title: 'Responder la pregunta',
  answer_placeholder: 'Escribe tu respuesta…',
  answer_submit: 'Enviar',
  answer_submitting: 'Enviando…',
  answer_back: 'Volver',
  answer_error_send: 'No se pudo enviar la respuesta.',

  // SHARE SCREEN
  share_title: 'Respuesta publicada ✅',
  share_hint: 'Comparte la tarjeta y consigue aún más preguntas.',
  share_card_rendering_desc:
    'La tarjeta aún se está generando. Vuelve a las preguntas y revisa en breve — podrás compartirla en una Story o por chat.',
  share_card_alt: 'Tarjeta de respuesta',
  share_story_btn: 'Compartir en Story',
  share_story_btn_busy: 'Abriendo…',
  share_chat_btn: 'Compartir por chat',
  share_chat_btn_busy: 'Preparando…',
  share_back: 'Volver a preguntas',
  share_story_unsupported:
    'Las Stories no están disponibles en esta versión de Telegram. Prueba "Compartir por chat".',
  share_story_error: 'No se pudo abrir la Story. Prueba "Compartir por chat".',
  share_chat_sent: 'Mensaje enviado.',
  share_chat_unsupported:
    'El envío por chat no está disponible en esta versión de Telegram. Actualiza tu cliente.',
  share_chat_error: 'No se pudo compartir. Inténtalo de nuevo.',
  share_chat_prepare_error: 'No se pudo preparar el mensaje.',

  // LEADERBOARD SCREEN
  lb_title: 'Clasificación',
  lb_period_7d: '7 días',
  lb_period_30d: '30 días',
  lb_period_all: 'Siempre',
  lb_group_label: 'Categoría',
  lb_group_all: 'Todos',
  lb_group_ask: 'Preguntas',
  lb_group_rate: 'Valoraciones',
  lb_group_confession: 'Confesiones',
  lb_group_guesswho: 'Adivina quién',
  lb_group_message: 'Mensajes',
  lb_error: 'No se pudo cargar la clasificación',
  lb_empty: 'Nadie en el ranking aún',
  lb_anon: 'Anónimo',
  lb_load_more: 'Cargar más',
  lb_questions: (n) => plural('es', n, { one: '{n} pregunta', other: '{n} preguntas' }),
  lb_referrals: (n) => plural('es', n, { one: '{n} ref.', other: '{n} refs.' }),

  // REWARDS SCREEN
  rw_title: 'Mis recompensas',
  rw_error: 'No se pudieron cargar las recompensas',
  rw_level: (n) => `Nivel ${n}`,
  rw_confirmed_referrals: (n) =>
    plural('es', n, { one: '{n} referido confirmado', other: '{n} referidos confirmados' }),
  rw_next_level_section: (n) =>
    plural('es', n, { one: 'SIGUIENTE NIVEL — {n} referido', other: 'SIGUIENTE NIVEL — {n} referidos' }),
  rw_to_level: (level, target) => `al nivel ${level}: ${target}`,
  rw_max_level: '¡Nivel máximo alcanzado!',
  rw_badges_section: 'INSIGNIAS',
  rw_formats_section: 'FORMATOS DE BOX',

  // QUESTION CARD
  qcard_status_new: 'Nueva',
  qcard_status_hidden: 'Oculta',
  qcard_status_answered: 'Respondida',
  qcard_moderation_flagged: 'Atención: mensaje sospechoso',
  qcard_answer_btn: 'Responder',
  qcard_answer_send: 'Enviar',
  qcard_share_btn: 'Compartir',
  qcard_hide_btn: 'Ocultar',
  qcard_show_btn: 'Mostrar',
  qcard_report_btn: 'Reportar',
  qcard_block_btn: 'Bloquear',
  qcard_unblock_btn: 'Desbloquear',
  qcard_report_reason_label: 'Selecciona el motivo del reporte:',
  qcard_reason_spam: 'Spam',
  qcard_reason_harassment: 'Acoso',
  qcard_reason_sexual: 'Contenido sexual',
  qcard_reason_other: 'Otro',
  qcard_error_hide: 'No se pudo cambiar la visibilidad.',
  qcard_error_report: 'No se pudo enviar el reporte.',
  qcard_error_block: 'No se pudo bloquear al remitente.',
  qcard_report_done: 'Reporte enviado — gracias.',
  qcard_block_done: 'Remitente bloqueado.',
  qcard_unblock_done: 'Remitente desbloqueado.',

  // ERRORS
  err_network: 'Sin conexión al servidor. Comprueba tu internet e inténtalo de nuevo.',
  err_server_unavailable: 'El servidor no está disponible temporalmente, inténtalo más tarde.',
  err_internal: 'Algo salió mal de nuestro lado. Ya estamos revisando — inténtalo más tarde.',
  err_bad_request: 'Por favor, revisa los datos que ingresaste.',
  err_generic: 'No se pudo completar la acción, inténtalo más tarde.',
  err_schema: 'Error de datos. Intenta actualizar la app.',
  share_story_link_premium_note:
    'Un enlace clicable en Stories requiere Telegram Premium. Sin él tus amigos igual te encuentran — tu usuario aparece en la tarjeta.',

  // AUTH ERROR SCREEN
  auth_error_title: 'Error de acceso',
  auth_error_desc:
    'No se pudo autenticar. Por favor, cierra y vuelve a abrir la Mini App desde Telegram.',

  // REWARD TOAST
  reward_toast_title: '¡Nueva recompensa!',
  reward_toast_close: '×',

  // BOTTOM TABBAR
  tab_questions: 'Preguntas',
  tab_leaderboard: 'Clasificación',
  tab_rewards: 'Recompensas',
  tab_settings: 'Ajustes',

  // SETTINGS SCREEN
  settings_title: 'Ajustes',
  settings_section_general: 'General',
  settings_notifications: 'Notificaciones de nuevas preguntas',
  settings_notifications_hint: 'El bot te avisará cuando alguien te haga una pregunta.',
  settings_notifications_error: 'No se pudo guardar la configuración de notificaciones.',
  settings_language: 'Idioma',
  settings_language_error: 'No se pudo cambiar el idioma. Inténtalo de nuevo.',
  settings_section_about: 'Acerca de',
  settings_version: 'Versión',

  // CODE ENTRY (work-020)
  code_modal_title: 'Los enlaces en Stories son solo para Premium',
  code_modal_explain:
    'Telegram solo da un enlace clicable en Stories a los suscriptores Premium. En cambio, añade tu código a la story — los visitantes lo escriben en la app y te hacen una pregunta anónima.',
  code_modal_your_code: 'Tu código',
  code_modal_copy: 'Copiar código',
  code_modal_copied: 'Copiado',
  code_modal_share_btn: 'Compartir en story',
  code_modal_loading: 'Obteniendo tu código…',
  code_modal_error: 'No se pudo obtener tu código. Inténtalo más tarde.',
  ask_code_title: 'Introduce un código',
  ask_code_desc:
    '¿Llegaste desde una story? Encuentra el código en ella (ej. 1234) y escríbelo abajo.',
  ask_code_placeholder: 'Código de la story',
  ask_code_find: 'Buscar',
  ask_code_not_found: 'Código no encontrado. Comprueba los dígitos e inténtalo de nuevo.',
  ask_code_invalid: 'El código tiene 4 dígitos.',
  code_onboarding_header: '¿Llegaste desde una story?',
  code_onboarding_text: 'Introduce el código de un amigo para hacerle una pregunta anónima.',
  code_onboarding_action: 'Introducir código',
  settings_ask_by_code: 'Preguntar por código',
  settings_my_code_title: 'Tu código para stories',
  settings_my_code_hint:
    'Escribe este código en tu story — los visitantes lo introducen para hacerte una pregunta anónima. Telegram solo da enlaces clicables en Stories a los suscriptores Premium.',
  settings_my_code_rotate: 'Obtener nuevo código',
  settings_my_code_rotating: 'Actualizando…',
  settings_my_code_done: 'Código actualizado',
  settings_my_code_error: 'No se pudo obtener tu código.',
  settings_my_code_rotate_confirm_title: '¿Cambiar tu código?',
  settings_my_code_rotate_confirm_desc:
    'Tu código anterior deja de funcionar al instante. Quien lo vio en tu story ya no podrá escribirte. Cámbialo solo si alguien te está spameando.',
  settings_my_code_rotate_confirm_action: 'Cambiar código',

  // BOT
  bot_greeting: (name) => (name ? `Hola, ${name}! 👋` : 'Hola! 👋'),
  bot_onboarding:
    'Esto es VeylMe — preguntas anónimas.\n\n' +
    'Abre la Mini App, publica una tarjeta «Pregúntame de forma anónima» en tu Story — ' +
    'y recibe preguntas anónimas justo aquí.\n\n' +
    '⚠️ 18+. Sé respetuoso: el acoso, las amenazas y el contenido prohibido ' +
    'no están permitidos. Anonimato ≠ impunidad — las infracciones van a moderación.',
  bot_open_btn: 'Abrir VeylMe',
  notify_answer_btn: 'Responder',
  bot_invite_text:
    'Alguien ha compartido un enlace de VeylMe.\n\n' +
    'Abre la Mini App para hacer una pregunta anónima:',
  bot_invite_btn: 'Preguntar de forma anónima',

  // WORK-027 i18n sweep
  tagline: 'Pregúntame de forma anónima',
  share_ask_action: 'Preguntar',
  share_answered_text: 'Respondí una pregunta anónima 👀',
  err_not_in_telegram: 'La Mini App no se está ejecutando en Telegram. Reinicia la app.',
  badge_viral_starter: 'Inicio viral',
  badge_storyteller: 'Narrador',
  badge_loop_master: 'Maestro del ciclo',
  theme_confession: 'Confesiones',
  theme_guesswho: 'Adivina quién',
  rw_format_named: (name) => `formato «${name}»`,
  card_label_question: 'Pregunta',
  card_label_answer: 'Respuesta',
  card_invite_subtitle: 'anónimo, sin registro',
  card_code_hint: 'INGRESA EL CÓDIGO EN LA APP',
  share_msg_title: (brand) => `${brand} · Preguntas anónimas`,
  notify_new_question: '🔔 Alguien te hizo una pregunta anónima',
  notify_unanswered_reminder: (n) =>
    plural('es', n, { one: '🔔 Tienes {n} pregunta sin responder', other: '🔔 Tienes {n} preguntas sin responder' }),
  notify_ban_permanent: 'permanentemente',
  notify_ban_until: (date) => `hasta el ${date}`,
  notify_ban_msg: (untilPhrase) =>
    `⛔ Estás bloqueado en VeylMe ${untilPhrase}. Demasiados destinatarios reportaron tus mensajes.\nSi crees que esto es un error, puedes enviar una apelación en la app.`,
  notify_warning_msg:
    '⚠️ Varios destinatarios reportaron tus mensajes. Un poco más y serás bloqueado en VeylMe.',

  // MY QUESTIONS SCREEN + InvitePanel non-premium hint (retention-answer-loop)
  my_questions_title: 'Mis preguntas',
  my_questions_empty_title: 'Todavía no has hecho ninguna pregunta',
  my_questions_empty_desc: 'Haz una pregunta en el buzón de alguien — la respuesta aparecerá aquí.',
  my_questions_loading: 'Comprobando si hay respuesta…',
  my_questions_waiting: 'Esperando respuesta',
  my_questions_answer_label: 'Respuesta',
  settings_my_questions: 'Mis preguntas',
  ask_sent_find_reply_hint: 'Podrás encontrar la respuesta más tarde en «Mis preguntas».',
  inbox_story_nonpremium_hint: 'Un enlace de Story no es clicable sin Telegram Premium, por eso mostramos un código — compartir en un chat es más fiable.',

  // SESSION EXPIRY SCREEN (work-038)
  session_expired_title: 'Tu pase de Telegram ha caducado',
  session_expired_desc: 'Telegram entrega a la app un pase solo una vez, al abrirla, y este ya ha caducado. Abre la app de nuevo — hemos guardado el texto que empezaste.',
  session_expired_desc_no_storage: 'Tu pase de Telegram ha caducado y hay que volver a abrir la app. No hemos podido guardar el texto que escribiste — cópialo antes de salir.',
  session_expired_reopen: 'Abrir de nuevo',
  session_expired_stuck_title: 'Telegram devolvió el mismo pase antiguo',
  session_expired_stuck_desc: 'Cierra la app por completo desde el menú del bot y ábrela otra vez — entonces Telegram entregará un pase nuevo.',
  session_auth_failed_title: 'No se pudo confirmar tu acceso',
  session_auth_failed_desc: 'Telegram no confirmó tu acceso a la app. Cierra la app y ábrela de nuevo desde el chat con el bot.',
  session_expired_manual_close: 'La app no puede cerrarse sola aquí — ciérrala tú y ábrela de nuevo desde el chat con el bot.',
  session_expiring_soon: 'Tu pase de Telegram caducará pronto. Abre la app de nuevo para no perder nada.',
  my_questions_check_failed: 'No se pudieron comprobar las respuestas. Inténtalo más tarde.',
  settings_notifications_load_error: 'No se pudo cargar el ajuste.',
};
